import { Grid, Button, CircularProgress, Box, Chip, Typography } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { GRAY_COLORS } from 'asterX-UI'
import { a11yProps } from 'asterX-UI/Tabs/TabPanel'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AsterTabs from 'asterX-UI/Tabs/AsterTabs'
import AsterTab from 'asterX-UI/Tabs/AsterTab'
import { PageContainer } from 'components/PageContainer'
import { useRoutes } from 'lib/hooks'
import { useLocation } from 'react-router'
import { useAuthStore } from 'context/AuthStore'
import { useHomeStore } from 'context/HomeStore'
import { adminRoutes, lawyerRoutes, managerRoutes } from 'routes/constants.routes'
import { SessionStorageService } from 'lib/utils'
import NoMatch from 'pages/NoMatch/NoMatch'
import ExcelUploadIcon from '../../asterX-UI/Icons/ExcelUploadIcon'
import ActionModal from '../../asterX-UI/Modals/ActionModal'

const useStyles = {
  tabContainer: {
    borderBottom: `1px solid ${GRAY_COLORS[200]}`,
    margin: '24px 0 36px 0',
    overflow: 'hidden'
  }
}

export const MainTabs = ({ children }) => {
  const { authStore, authStoreFunc } = useAuthStore()
  const routes = useRoutes()
  const location = useLocation()
  const tabRef = useRef(null)
  const { homeStore, homeStoreFunc } = useHomeStore()
  const compact = useMediaQuery('(max-width: 1125px)')

  const pad = n => (n < 10 ? '0' : '') + n
  const YearMonthFromDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`
  const YearMonthToRuLabel = ym => {
    const dt = new Date(ym + '-01')
    const s = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(dt)
    return s.charAt(0).toUpperCase() + s.slice(1)
  }

  const now = new Date()
  const latestDate = new Date(now.getFullYear(), now.getMonth(), 1)
  const earliestDate = new Date(now.getFullYear(), now.getMonth() - 5, 1)
  const currentMonth = YearMonthFromDate(latestDate)
  const latestYearMonth = YearMonthFromDate(latestDate)
  const earliestYearMonth = YearMonthFromDate(earliestDate)

  const YearMonthIndex = YearMonth => {
    const [y, m] = YearMonth.split('-').map(Number)
    return y * 12 + (m - 1)
  }

  const isYearMonthInWindow = YearMonth =>
    YearMonthIndex(YearMonth) >= YearMonthIndex(earliestYearMonth) &&
    YearMonthIndex(YearMonth) <= YearMonthIndex(latestYearMonth)

  const getPreviousFullMonths = (n, base = new Date()) => {
    const cursor = new Date(base.getFullYear(), base.getMonth(), 1)
    cursor.setMonth(cursor.getMonth() - 1)
    const acc = []
    for (let i = 0; i < n; i++) {
      acc.push(YearMonthFromDate(cursor))
      cursor.setMonth(cursor.getMonth() - 1)
    }
    return acc
  }

  const previousFullMonths = useMemo(() => getPreviousFullMonths(3, now), [])

  const [selectedMonth, setSelectedMonth] = useState('')
  const [isCustomMonth, setIsCustomMonth] = useState(false)
  const [isResultsMode, setIsResultsMode] = useState(false)
  const [isMonthExportOpen, setIsMonthExportOpen] = useState(false)

  // Роуты
  const privateRoutes = useMemo(() => {
    if (authStoreFunc.isAdmin()) return adminRoutes
    if (authStoreFunc.isManager()) return managerRoutes(authStoreFunc.isSoftManager())
    if (authStoreFunc.isLawyer()) return lawyerRoutes
    return []
  }, [authStoreFunc])

  const [tab, setTab] = useState(privateRoutes[0]?.path || '')

  // Эффекты
  useEffect(() => {
    const pathname = location.pathname
    const current = privateRoutes.find(e => pathname.includes(e.path))
    if (current) setTab(current.path)
  }, [location, privateRoutes])

  useEffect(() => {
    if (authStore.isMobile && tabRef.current) {
      setTimeout(() => {
        const activeTab =
          tabRef.current.querySelector('.Mui-selected') || tabRef.current.querySelector('[aria-selected="true"]')
        if (activeTab) {
          activeTab.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          })
        }
      }, 10)
    }
  }, [tab, authStore.isMobile])


  const handleChangeTab = newValue => {
    setTab(newValue)
    SessionStorageService.clear()
  }

  const addMonth = date => {
    if (!date) return
    const YearMonth = YearMonthFromDate(date)
    if (!isYearMonthInWindow(YearMonth)) return
    setSelectedMonth(YearMonth)
  }

  const selectPresetMonth = YearMonth => {
    if (!isYearMonthInWindow(YearMonth) || isCustomMonth || isResultsMode) return
    setSelectedMonth(prev => (prev === YearMonth ? '' : YearMonth))
  }

  const openExportModal = () => {
    setSelectedMonth('')
    setIsCustomMonth(false)
    setIsResultsMode(false)
    setIsMonthExportOpen(true)
  }

  const handleToggleResultsMode = () => {
    const newMode = !isResultsMode
    setIsResultsMode(newMode)
    setSelectedMonth(newMode ? currentMonth : '')
    setIsCustomMonth(false)
  }

  const handleConfirmExport = () => {
    if (!selectedMonth) return

    if (isResultsMode) {
      homeStoreFunc?.handleExportWorkListResults({ date: selectedMonth })
    } else {
      homeStoreFunc?.handleExportAllWorkList({ date: selectedMonth })
    }

    setIsMonthExportOpen(false)
  }

  const getModalTitle = () =>
    isResultsMode ? 'Выгрузка результатов за выбранный месяц' : 'Выгрузка за выбранный месяц'

  const canConfirmExportMonth = !!selectedMonth && !homeStore?.loading

  const getChipStyle = (isActive, isDisabled) => ({
    borderRadius: 24,
    opacity: isDisabled ? 0.6 : 1,
    pointerEvents: isDisabled ? 'none' : 'auto'
  })

  if (privateRoutes.length === 0) return <NoMatch />

  const showExportButton = (authStoreFunc.isAdmin() || authStoreFunc.isManager()) && tab !== '/applications'
  const isChipDisabled = isCustomMonth || isResultsMode

  return (
    <PageContainer>
      <Grid container alignItems="center" justifyContent="space-between" style={useStyles.tabContainer} spacing={2}>
        <Grid item xs={12} md style={{ overflow: 'auto' }} ref={tabRef}>
          <AsterTabs variant="scrollable" value={tab} onChange={(e, newValue) => handleChangeTab(newValue)}>
            {privateRoutes.map((route, index) => (
              <AsterTab
                key={`rop-tab-${route.path}-${index}`}
                label={route.label || ''}
                value={route.path}
                {...a11yProps(index)}
                onClick={() => routes.goTo(route.path)}
              />
            ))}
          </AsterTabs>
        </Grid>

        {showExportButton && (
          <Grid item xs="auto">
            <Button
              variant="outlined"
              color="primary"
              style={{ minWidth: compact ? 48 : 220, height: 48, fontWeight: 600, transition: 'all .2s' }}
              onClick={openExportModal}
              title="Выгрузить Excel по месяцам"
              startIcon={<ExcelUploadIcon />}
              disabled={homeStore?.loading}
            >
              {homeStore?.loading ? (
                <>
                  <CircularProgress size={18} style={{ marginRight: 8 }} />
                  {!compact && 'Экспорт…'}
                </>
              ) : (
                !compact && 'Выгрузить в Excel'
              )}
            </Button>
          </Grid>
        )}
      </Grid>

      <ActionModal
        open={isMonthExportOpen}
        onClose={() => setIsMonthExportOpen(false)}
        onCancel={() => setIsMonthExportOpen(false)}
        onConfirm={handleConfirmExport}
        title={getModalTitle()}
        labelCancel="Отмена"
        labelConfirm="Выгрузить"
        disableConfirm={!canConfirmExportMonth}
        color="primary"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
              <Chip
                label="Текущий месяц"
                color={selectedMonth === currentMonth && !isResultsMode ? 'primary' : 'default'}
                variant={selectedMonth === currentMonth && !isResultsMode ? 'default' : 'outlined'}
                onClick={!isChipDisabled ? () => selectPresetMonth(currentMonth) : undefined}
                style={getChipStyle(selectedMonth === currentMonth, isChipDisabled)}
              />
              {previousFullMonths.map(month => {
                const isActive = selectedMonth === month
                return (
                  <Chip
                    key={month}
                    label={YearMonthToRuLabel(month)}
                    color={isActive && !isResultsMode ? 'primary' : 'default'}
                    variant={isActive && !isResultsMode ? 'default' : 'outlined'}
                    onClick={!isChipDisabled ? () => selectPresetMonth(month) : undefined}
                    style={getChipStyle(isActive, isChipDisabled)}
                  />
                )
              })}
              <Chip
                label={isCustomMonth ? 'Свой (вкл.)' : 'Свой'}
                color={isCustomMonth ? 'primary' : 'default'}
                variant={isCustomMonth ? 'default' : 'outlined'}
                onClick={!isResultsMode ? () => setIsCustomMonth(v => !v) : undefined}
                style={getChipStyle(isCustomMonth, isResultsMode)}
              />
              <Chip
                label={isResultsMode ? 'Текущий результат (вкл)' : 'Текущий результат'}
                color={isResultsMode ? 'primary' : 'default'}
                variant={isResultsMode ? 'default' : 'outlined'}
                onClick={handleToggleResultsMode}
                style={{ borderRadius: 24 }}
              />
            </Box>
          </Grid>

          {isCustomMonth && !isResultsMode && (
            <Grid item xs={12} sm={6}>
              <DatePicker
                views={['year', 'month']}
                openTo="month"
                variant="dialog"
                inputVariant="outlined"
                autoOk
                label="Выбрать месяц"
                format="MM.yyyy"
                value={selectedMonth ? new Date(selectedMonth + '-01') : null}
                onChange={addMonth}
                fullWidth
                disableFuture
                minDate={earliestDate}
                maxDate={latestDate}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="caption" color={!selectedMonth || canConfirmExportMonth ? 'textSecondary' : 'error'}>
              {isResultsMode
                ? `Будет использован текущий месяц: ${YearMonthToRuLabel(currentMonth)}.`
                : `Доступный период: ${YearMonthToRuLabel(earliestYearMonth)} — ${YearMonthToRuLabel(
                    latestYearMonth
                  )}.${selectedMonth ? ` Выбрано: ${YearMonthToRuLabel(selectedMonth)}.` : ' Месяц не выбран.'}`}
            </Typography>
            {selectedMonth && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 8 }}>
                <Chip
                  label={YearMonthToRuLabel(selectedMonth)}
                  onDelete={!isResultsMode ? () => setSelectedMonth('') : undefined}
                  color="default"
                  style={{ borderRadius: 20 }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </ActionModal>

      {children}
    </PageContainer>
  )
}
